import { BookmarkIcon, CheckIcon, ChevronDownIcon, SaveIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { DataViewController } from "~/views/viewController";
import { cn } from "~/lib/utils";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { PLANNING_DISPLAY_SECTION_LABEL } from "./planningDisplayChrome";
import { BaselineViewRow, NewViewButton, SavedViewRow, ViewRenameForm } from "./SavedViewRows";

interface SavedViewsSectionProps {
  readonly controller: DataViewController;
  readonly className?: string;
  readonly showDirtyActions?: boolean;
}

function SavedViewsSection({
  controller,
  className,
  showDirtyActions = true,
}: SavedViewsSectionProps) {
  const [saveFormOpen, setSaveFormOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const activeView = controller.activeSavedView;

  const saveAsNew = async () => {
    const name = draftName.trim();
    if (name.length === 0) return;
    const saved = await controller.saveView({
      name,
      state: controller.state,
      setDefault: controller.savedViews.length === 0,
    });
    if (!saved) return;
    setDraftName("");
    setSaveFormOpen(false);
  };

  return (
    <section className={className} aria-label="Saved views">
      <div className={cn("flex items-center justify-between", PLANNING_DISPLAY_SECTION_LABEL)}>
        <span>Views</span>
        {controller.isLoading ? (
          <span className="normal-case tracking-normal text-muted-foreground/70">Loading…</span>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        <BaselineViewRow isActive={!activeView} onSelect={controller.resetState} />
        {controller.savedViews.map((view) =>
          renamingId === view.id ? (
            <ViewRenameForm
              key={view.id}
              initialName={view.name}
              disabled={controller.isMutating}
              onCommit={(name) => {
                void controller.renameView(view.id, name);
                setRenamingId(null);
              }}
              onCancel={() => setRenamingId(null)}
            />
          ) : (
            <SavedViewRow
              key={view.id}
              view={view}
              isActive={activeView?.id === view.id}
              disabled={controller.isMutating}
              onSelect={() => controller.applySavedView(view.id)}
              onToggleDefault={() =>
                void controller.setDefaultView(view.isDefault ? null : view.id)
              }
              onRename={() => setRenamingId(view.id)}
              onDelete={() => void controller.deleteView(view.id)}
            />
          ),
        )}
        {saveFormOpen ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void saveAsNew();
            }}
            className="flex items-center gap-1 px-1"
          >
            <input
              aria-label="Saved view name"
              value={draftName}
              disabled={controller.isMutating}
              onChange={(event) => setDraftName(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                event.stopPropagation();
                setDraftName("");
                setSaveFormOpen(false);
              }}
              placeholder="Name this view…"
              autoFocus
              className="h-7 min-w-0 flex-1 rounded-sm border border-border bg-background px-2 text-xs text-foreground outline-none placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={draftName.trim().length === 0 || controller.isMutating}
              aria-label="Create saved view"
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-45"
            >
              <SaveIcon className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setDraftName("");
                setSaveFormOpen(false);
              }}
              aria-label="Cancel saved view"
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <XIcon className="size-3.5" />
            </button>
          </form>
        ) : (
          <NewViewButton onClick={() => setSaveFormOpen(true)} disabled={controller.isMutating} />
        )}
      </div>
      {showDirtyActions && controller.isDirty && activeView ? (
        <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
          <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
            Unsaved changes to “{activeView.name}”
          </span>
          <button
            type="button"
            onClick={() => controller.applySavedView(activeView.id)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Reset
          </button>
          <button
            type="button"
            disabled={controller.isMutating}
            onClick={() => void controller.updateActiveView()}
            className="inline-flex h-6 items-center rounded-md bg-foreground px-2.5 text-xs font-medium text-background disabled:opacity-50"
          >
            Update
          </button>
        </div>
      ) : null}
      {controller.error ? (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-destructive-surface px-2 py-1.5 text-xs text-destructive">
          <span className="min-w-0 flex-1">{controller.error}</span>
          <button
            type="button"
            onClick={() => void controller.refresh()}
            className="shrink-0 underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Full saved-views chrome used by Projects filter and task Display options:
 * the views list plus the unsaved-changes footer from the Projects page.
 */
export function SavedViewsWidget({
  controller,
  className,
}: {
  readonly controller: DataViewController;
  readonly className?: string;
}) {
  const dirtyView = controller.isDirty ? controller.activeSavedView : null;
  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <SavedViewsSection
        controller={controller}
        showDirtyActions={false}
        className="min-h-0 flex-1 overflow-y-auto"
      />
      {dirtyView ? (
        <div className="flex items-center gap-2 border-t border-border/40 bg-muted/35 px-2.5 py-1.5">
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="size-1.5 shrink-0 rounded-full bg-warning/90" aria-hidden="true" />
            <span className="truncate">Unsaved changes to “{dirtyView.name}”</span>
          </span>
          <button
            type="button"
            onClick={() => controller.applySavedView(dirtyView.id)}
            className="inline-flex h-6 shrink-0 items-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => void controller.updateActiveView()}
            className="inline-flex h-6 shrink-0 items-center rounded-md bg-foreground px-2.5 text-xs font-medium text-background transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Update view
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SavedViewsPopover({
  controller,
  triggerVariant = "default",
}: {
  readonly controller: DataViewController;
  /** `icon` is a Linear-style circular control for project tabs trailing. */
  readonly triggerVariant?: "default" | "icon";
}) {
  const [open, setOpen] = useState(false);
  const iconTrigger = triggerVariant === "icon";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Saved views"
        title={controller.activeSavedView?.name ?? "Saved views"}
        className={
          iconTrigger
            ? "inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/40 text-muted-foreground transition-colors duration-150 hover:border-border hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[popup-open]:border-transparent data-[popup-open]:bg-muted data-[popup-open]:text-foreground"
            : "inline-flex min-h-touch min-w-touch items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-8 md:min-h-0"
        }
      >
        {controller.activeSavedView ? (
          <CheckIcon className="size-3.5" aria-hidden />
        ) : (
          <BookmarkIcon className="size-3.5" aria-hidden />
        )}
        {iconTrigger ? null : (
          <>
            <span className="hidden max-w-28 truncate md:inline">
              {controller.activeSavedView?.name ?? "Views"}
            </span>
            <ChevronDownIcon className="size-3" aria-hidden />
          </>
        )}
      </PopoverTrigger>
      <PopoverPopup align="end" sideOffset={6} className="w-[280px] p-1">
        <SavedViewsSection controller={controller} className="max-h-[360px] overflow-y-auto" />
      </PopoverPopup>
    </Popover>
  );
}
